import { useEffect, useState } from 'react';
import { Form, Input, Button, Upload, App as AntdApp, Row, Col, Divider, Select, Space } from 'antd';
import type { UploadFile } from 'antd';
import { Save, UploadCloud, Trash2, Building2 } from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { systemSettingsService } from '@/services/systemSettingsService';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';

export function SystemSettingsPage() {
  const { message, modal } = AntdApp.useApp();
  const { settings, hasLogo, logoVersion, refresh, bumpLogoVersion } = useSystemSettings();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<UploadFile | null>(null);

  useEffect(() => {
    form.setFieldsValue(settings);
  }, [settings, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await systemSettingsService.update(values);
      await refresh();
      message.success('Đã lưu cấu hình');
    } catch (e: any) {
      if (e.response) message.error(e.response?.data?.message ?? 'Không lưu được');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      message.error('File vượt quá 2MB');
      return false;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      message.error('Chỉ chấp nhận PNG, JPG, SVG, WebP');
      return false;
    }
    setUploading(true);
    try {
      await systemSettingsService.uploadLogo(file);
      bumpLogoVersion();
      await refresh();
      message.success('Đã upload logo');
      setLogoFile(null);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Upload thất bại');
    } finally {
      setUploading(false);
    }
    return false; // không dùng Upload auto, đã xử lý thủ công
  };

  const handleDeleteLogo = () => {
    modal.confirm({
      title: 'Xóa logo công ty?',
      content: 'Sau khi xóa, hệ thống sẽ hiển thị chữ viết tắt thay cho logo.',
      okText: 'Xóa',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await systemSettingsService.deleteLogo();
          bumpLogoVersion();
          await refresh();
          message.success('Đã xóa logo');
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Không xóa được');
        }
      },
    });
  };

  return (
    <>
      <PageHeader
        title="Cài đặt hệ thống"
        subtitle="Logo công ty, tên công ty, tên hệ thống"
      />

      <Row gutter={16}>
        {/* Logo upload */}
        <Col xs={24} md={8}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Logo công ty</div>
            <div style={{
              width: '100%',
              aspectRatio: '1 / 1',
              maxWidth: 200,
              margin: '0 auto 16px',
              border: '2px dashed #d4d9df',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fafbfc',
              overflow: 'hidden',
            }}>
              {hasLogo ? (
                <img
                  src={systemSettingsService.logoUrl(true) + `&v=${logoVersion}`}
                  alt="Logo"
                  style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#a5b0c0' }}>
                  <Building2 size={42} />
                  <div style={{ fontSize: 11, marginTop: 8 }}>Chưa có logo</div>
                </div>
              )}
            </div>
            <Upload
              showUploadList={false}
              fileList={logoFile ? [logoFile] : []}
              beforeUpload={(file) => {
                handleUploadLogo(file);
                return false;
              }}
              accept=".png,.jpg,.jpeg,.svg,.webp,image/*"
            >
              <Button
                icon={<UploadCloud size={14} />}
                loading={uploading}
                block
              >
                {hasLogo ? 'Đổi logo' : 'Upload logo'}
              </Button>
            </Upload>
            {hasLogo && (
              <Button
                danger
                icon={<Trash2 size={14} />}
                onClick={handleDeleteLogo}
                block
                style={{ marginTop: 8 }}
              >
                Xóa logo
              </Button>
            )}
            <p style={{ fontSize: 11, color: '#8a95a5', marginTop: 12, marginBottom: 12 }}>
              Định dạng: PNG, JPG, SVG, WebP. Kích thước tối đa 2MB.<br />
              Khuyến nghị: vuông 512×512px, nền trong suốt.
            </p>
            <Form form={form} layout="vertical">
              <Form.Item
                label="Chế độ hiển thị logo"
                name="logo_display_mode"
                extra="Nếu logo upload bị lỗi font/hình, chuyển sang 'Chữ viết tắt' để dùng tên viết tắt thay vì hình"
              >
                <Select
                  options={[
                    { value: 'image', label: 'Dùng logo upload (mặc định)' },
                    { value: 'text', label: 'Dùng chữ viết tắt (vd: HV)' },
                  ]}
                />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Textual settings */}
        <Col xs={24} md={16}>
          <Card>
            <Form form={form} layout="vertical">
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Thông tin công ty</div>

              <Form.Item
                label="Tên đầy đủ của công ty / bệnh viện"
                name="company_name"
                rules={[{ required: true, message: 'Nhập tên công ty' }]}
                extra="Hiển thị ở trang đăng nhập và các nơi cần tên đầy đủ"
              >
                <Input placeholder="Ví dụ: Bệnh viện Hưng Việt" maxLength={200} />
              </Form.Item>

              <Form.Item
                label="Tên viết tắt"
                name="company_short_name"
                rules={[{ required: true, message: 'Nhập viết tắt', max: 10 }]}
                extra="2-4 ký tự, hiển thị trên sidebar khi không có logo (VD: HV, BVHV)"
              >
                <Input placeholder="HV" maxLength={10} style={{ width: 200 }} />
              </Form.Item>

              <Divider />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Hệ thống phần mềm</div>

              <Form.Item
                label="Tên hệ thống"
                name="system_name"
                rules={[{ required: true, message: 'Nhập tên hệ thống' }]}
                extra="Hiển thị ở sidebar + tiêu đề trình duyệt + trang đăng nhập"
              >
                <Input placeholder="HV-TaskFlow" maxLength={100} />
              </Form.Item>

              <Form.Item
                label="Mô tả phụ"
                name="system_subtitle"
                extra="Hiển thị dưới tên hệ thống ở trang đăng nhập"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Hệ thống quản lý công việc & ký điện tử"
                  maxLength={200}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="Màu chủ đạo"
                name="primary_color"
                extra="Mã hex (VD: #1976D2). Ảnh hưởng accent trên toàn hệ thống và trang đăng nhập."
              >
                <Input
                  placeholder="#1976D2"
                  maxLength={9}
                  style={{ width: 200, fontFamily: 'monospace' }}
                  addonAfter={
                    <input
                      type="color"
                      style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer' }}
                      value={form.getFieldValue('primary_color') || '#1976D2'}
                      onChange={(e) => form.setFieldValue('primary_color', e.target.value)}
                    />
                  }
                />
              </Form.Item>

              <Form.Item
                label="Màu chủ đạo (đậm)"
                name="primary_color_dark"
                extra="Dùng cho gradient và các nút quan trọng. Mã hex (VD: #0F4C81)."
              >
                <Input
                  placeholder="#0F4C81"
                  maxLength={9}
                  style={{ width: 200, fontFamily: 'monospace' }}
                  addonAfter={
                    <input
                      type="color"
                      style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer' }}
                      value={form.getFieldValue('primary_color_dark') || '#0F4C81'}
                      onChange={(e) => form.setFieldValue('primary_color_dark', e.target.value)}
                    />
                  }
                />
              </Form.Item>

              <Divider />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Trang đăng nhập</div>

              <Form.Item
                label="Tiêu đề trang đăng nhập"
                name="login_title"
                extra="Hiện ngay phía trên form đăng nhập (in HOA)"
              >
                <Input placeholder="ĐĂNG NHẬP HỆ THỐNG" maxLength={100} />
              </Form.Item>

              <Form.Item
                label="Mô tả dưới tiêu đề"
                name="login_description"
                extra="1-2 câu hướng dẫn người dùng đăng nhập"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Nhập tên đăng nhập và mật khẩu được cấp bởi quản trị viên."
                  maxLength={300}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="Tên thương hiệu phụ (cột bên trái)"
                name="login_brand_subtitle"
                extra="VD: 'Cổng thông tin nội bộ', 'Phần mềm quản lý'"
              >
                <Input placeholder="Cổng thông tin nội bộ" maxLength={150} />
              </Form.Item>

              <Form.Item
                label="Mô tả ngắn cột thương hiệu"
                name="login_brand_caption"
                extra="Câu mô tả phía dưới tên thương hiệu"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Quản lý công việc & ký điện tử"
                  maxLength={300}
                  showCount
                />
              </Form.Item>

              <Divider />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Thông tin liên hệ</div>

              <Form.Item
                label="Địa chỉ"
                name="company_address"
                extra="Hiển thị ở footer trang đăng nhập"
              >
                <Input placeholder="34 Đại Cồ Việt, Hai Bà Trưng, Hà Nội" maxLength={300} />
              </Form.Item>

              <Form.Item
                label="Hotline"
                name="company_hotline"
                extra="Số điện thoại liên hệ"
              >
                <Input placeholder="(024) 3974 7777" maxLength={50} style={{ width: 280 }} />
              </Form.Item>

              <Form.Item
                label="Dòng Copyright"
                name="copyright_text"
                extra="Hiển thị cuối trang đăng nhập"
              >
                <Input placeholder="Copyright © 2026 Bệnh viện Hưng Việt" maxLength={300} />
              </Form.Item>

              <Divider />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Giao diện menu (Sidebar)</div>

              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: '#7d8a98', marginRight: 8 }}>Theme preset:</span>
                <Space size={6} wrap>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#1F2937',
                    menu_text_color: '#E5E7EB',
                    menu_active_color: '#1976D2',
                  })}>🌙 Tối (mặc định)</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#FFFFFF',
                    menu_text_color: '#1F2937',
                    menu_active_color: '#1976D2',
                  })}>☀️ Sáng</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#0F4C81',
                    menu_text_color: '#FFFFFF',
                    menu_active_color: '#FFA940',
                  })}>🏥 BV xanh</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#1E1B4B',
                    menu_text_color: '#E0E7FF',
                    menu_active_color: '#A78BFA',
                  })}>💜 Tím</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#064E3B',
                    menu_text_color: '#D1FAE5',
                    menu_active_color: '#34D399',
                  })}>🌿 Xanh lá</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#831843',
                    menu_text_color: '#FBCFE8',
                    menu_active_color: '#F472B6',
                  })}>🌸 Hồng đậm</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#7C2D12',
                    menu_text_color: '#FED7AA',
                    menu_active_color: '#FB923C',
                  })}>🍂 Cam đất</Button>
                  <Button size="small" onClick={() => form.setFieldsValue({
                    menu_bg_color: '#F3F4F6',
                    menu_text_color: '#374151',
                    menu_active_color: '#1976D2',
                  })}>⚪ Xám sáng</Button>
                </Space>
              </div>

              <Form.Item
                label="Màu nền menu"
                name="menu_bg_color"
                extra="Màu nền sidebar bên trái. Sáng (VD #f6f7f8) hoặc tối (VD #1F2937)."
              >
                <Input
                  placeholder="#1F2937"
                  maxLength={9}
                  style={{ width: 200, fontFamily: 'monospace' }}
                  addonAfter={
                    <input
                      type="color"
                      style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer' }}
                      value={form.getFieldValue('menu_bg_color') || '#1F2937'}
                      onChange={(e) => form.setFieldValue('menu_bg_color', e.target.value)}
                    />
                  }
                />
              </Form.Item>

              <Form.Item
                label="Màu chữ menu"
                name="menu_text_color"
                extra="Màu chữ menu bình thường. Auto-contrast với màu nền nếu để trống."
              >
                <Input
                  placeholder="#E5E7EB"
                  maxLength={9}
                  style={{ width: 200, fontFamily: 'monospace' }}
                  addonAfter={
                    <input
                      type="color"
                      style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer' }}
                      value={form.getFieldValue('menu_text_color') || '#E5E7EB'}
                      onChange={(e) => form.setFieldValue('menu_text_color', e.target.value)}
                    />
                  }
                />
              </Form.Item>

              <Form.Item
                label="Màu menu đang chọn"
                name="menu_active_color"
                extra="Màu highlight item menu đang chọn (background)."
              >
                <Input
                  placeholder="#1976D2"
                  maxLength={9}
                  style={{ width: 200, fontFamily: 'monospace' }}
                  addonAfter={
                    <input
                      type="color"
                      style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer' }}
                      value={form.getFieldValue('menu_active_color') || '#1976D2'}
                      onChange={(e) => form.setFieldValue('menu_active_color', e.target.value)}
                    />
                  }
                />
              </Form.Item>

              <Divider />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Font chữ hệ thống</div>

              <Form.Item
                label="Bộ font chữ"
                name="font_family"
                extra="Áp dụng cho toàn bộ giao diện. Chọn preset hoặc nhập custom."
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Chọn font..."
                  options={[
                    { label: '— Sans Serif hiện đại —', options: [
                      { value: 'Inter, system-ui, -apple-system, sans-serif', label: 'Inter (mặc định)' },
                      { value: '"Be Vietnam Pro", system-ui, sans-serif', label: 'Be Vietnam Pro (Việt hóa)' },
                      { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
                      { value: '"Open Sans", system-ui, sans-serif', label: 'Open Sans' },
                      { value: '"Noto Sans", system-ui, sans-serif', label: 'Noto Sans' },
                      { value: 'system-ui, -apple-system, sans-serif', label: 'System UI (theo OS)' },
                    ]},
                    { label: '— Serif (truyền thống) —', options: [
                      { value: '"Source Serif Pro", Georgia, serif', label: 'Source Serif Pro' },
                      { value: 'Georgia, serif', label: 'Georgia' },
                      { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
                    ]},
                    { label: '— Monospace —', options: [
                      { value: '"JetBrains Mono", "Fira Code", monospace', label: 'JetBrains Mono' },
                      { value: '"Courier New", Courier, monospace', label: 'Courier New' },
                    ]},
                  ]}
                />
              </Form.Item>

              <Form.Item
                label="Cỡ chữ cơ sở"
                name="font_size_base"
                extra="Áp dụng toàn bộ giao diện. Chọn preset hoặc nhập số 11-20."
              >
                <Select
                  placeholder="Chọn cỡ chữ..."
                  options={[
                    { value: '12', label: '12 px — Nhỏ (compact)' },
                    { value: '13', label: '13 px — Hơi nhỏ' },
                    { value: '14', label: '14 px — Mặc định' },
                    { value: '15', label: '15 px — Hơi to' },
                    { value: '16', label: '16 px — To' },
                    { value: '17', label: '17 px — Rất to' },
                    { value: '18', label: '18 px — Cực to' },
                  ]}
                />
              </Form.Item>

              {/* V1.0.28: Cấu hình khung chữ ký */}
              <div style={{
                marginTop: 24, paddingTop: 16, borderTop: '1px dashed #d4d9df',
                fontSize: 13, fontWeight: 600, marginBottom: 12,
              }}>
                ✍️ Khung chữ ký trong phiếu
              </div>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Chiều cao khung ký (px)"
                    name="signature_box_height"
                    extra="Khoảng trống để ký bên trên dòng họ tên."
                  >
                    <Select
                      placeholder="Chọn chiều cao..."
                      options={[
                        { value: '40',  label: '40 px — Rất nhỏ' },
                        { value: '50',  label: '50 px — Nhỏ' },
                        { value: '55',  label: '55 px — Mặc định' },
                        { value: '65',  label: '65 px — Vừa' },
                        { value: '80',  label: '80 px — To' },
                        { value: '100', label: '100 px — Rất to' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Chiều rộng khung ký (px)"
                    name="signature_box_width"
                    extra="Chiều rộng tối đa của mỗi cột ký. Nếu nhiều cột sẽ chia đều."
                  >
                    <Select
                      placeholder="Chọn chiều rộng..."
                      options={[
                        { value: '120', label: '120 px — Hẹp' },
                        { value: '150', label: '150 px — Vừa nhỏ' },
                        { value: '180', label: '180 px — Mặc định' },
                        { value: '220', label: '220 px — Rộng' },
                        { value: '280', label: '280 px — Rất rộng' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* V1.0.29: Cấu hình labels Ma trận Eisenhower */}
              <div style={{
                marginTop: 24, paddingTop: 16, borderTop: '1px dashed #d4d9df',
                fontSize: 13, fontWeight: 600, marginBottom: 12,
              }}>
                📊 Ma trận Eisenhower — Tên 4 ô (Q1-Q4) + trục
              </div>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="🔴 Q1 — Tên đầy đủ" name="eis_q1_title">
                    <Input placeholder="Q1 — QUAN TRỌNG VÀ KHẨN CẤP" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Q1 — Hành động (subtitle)" name="eis_q1_subtitle">
                    <Input placeholder="Ưu tiên thực hiện ngay" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Q1 — Tên ngắn (trong panel tổng hợp)" name="eis_q1_short">
                <Input placeholder="Quan trọng & Khẩn cấp" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="🟢 Q2 — Tên đầy đủ" name="eis_q2_title">
                    <Input placeholder="Q2 — QUAN TRỌNG NHƯNG KHÔNG KHẨN CẤP" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Q2 — Hành động (subtitle)" name="eis_q2_subtitle">
                    <Input placeholder="Lên kế hoạch thực hiện" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Q2 — Tên ngắn" name="eis_q2_short">
                <Input placeholder="Quan trọng, không khẩn" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="🔵 Q3 — Tên đầy đủ" name="eis_q3_title">
                    <Input placeholder="Q3 — KHÔNG QUAN TRỌNG VÀ KHÔNG KHẨN CẤP" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Q3 — Hành động (subtitle)" name="eis_q3_subtitle">
                    <Input placeholder="Có thể loại bỏ hoặc làm sau" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Q3 — Tên ngắn" name="eis_q3_short">
                <Input placeholder="Không quan trọng, không khẩn" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="🟡 Q4 — Tên đầy đủ" name="eis_q4_title">
                    <Input placeholder="Q4 — KHÔNG QUAN TRỌNG NHƯNG KHẨN CẤP" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Q4 — Hành động (subtitle)" name="eis_q4_subtitle">
                    <Input placeholder="Ủy quyền hoặc làm nhanh" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Q4 — Tên ngắn" name="eis_q4_short">
                <Input placeholder="Không quan trọng, khẩn" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Tên trục X (ngang)" name="eis_axis_x">
                    <Input placeholder="MỨC ĐỘ KHẨN CẤP" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Tên trục Y (dọc)" name="eis_axis_y">
                    <Input placeholder="MỨC ĐỘ QUAN TRỌNG" />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<Save size={14} />}
                  loading={saving}
                  onClick={handleSave}
                >
                  Lưu thay đổi
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
